import { ky } from './ky';

export type ProductStatus = 'DRAFT' | 'ON_SALE' | 'SUSPENDED';
export type ProductVisibility = 'PUBLIC' | 'PRIVATE';
export type ProductPurchasableReason =
  | 'PURCHASABLE'
  | 'DRAFT'
  | 'SUSPENDED'
  | 'SALE_NOT_STARTED'
  | 'SALE_ENDED'
  | 'SOLD_OUT';

/**
 * 피드 링크에 임베드되는 상품 요약 정보.
 * 백엔드 `FeedProductLinkDto`와 매칭된다. (v1/link/data/FeedLinkDto.product)
 */
export interface FeedProductLinkDto {
  id: number; // 상품 ID (상세 라우트 /products/{id}에 사용)
  title: string;
  thumbnail?: string | null;
  listPrice: number;
  salePrice: number;
  currencyCode: string;
  discountRate: number;
  purchasable: boolean;
}

export interface ProductVariantDto {
  id: number;
  optionName: string;
  optionValue: string;
  skuCode: string;
  stockQuantity: number;
  sellable: boolean;
}

/**
 * 상품 고시정보 항목. 백엔드 `ProductNoticeItemDto`와 매칭된다.
 */
export interface ProductNoticeItemDto {
  fieldCode: string;
  label: string;
  value?: string | null;
  required: boolean;
  sortOrder: number;
  inputType: string;
  regulationReference?: string | null;
}

/**
 * 상품 필수 고시정보. 백엔드 `ProductNoticeDto`와 매칭된다.
 */
export interface ProductNoticeDto {
  categoryCode: string;
  categoryName: string;
  templateVersion: number;
  items: ProductNoticeItemDto[];
}

/**
 * 상품 상세 DTO. 백엔드 `ProductDto`(v1/commerce/data/ProductDto.kt)와 매칭된다.
 * 상세 페이지(lone-frontend#8) 구현에 사용된다.
 */
export interface ProductDto {
  id: number;
  contentId: number;
  contentSid?: string | null;
  sellerAccountId: number;
  title: string;
  thumbnail?: string | null;
  description?: string | null;
  categoryCode: string;
  status: ProductStatus;
  visibility: ProductVisibility;
  purchasable: boolean;
  purchasableReason?: ProductPurchasableReason | null;
  listPrice: number;
  salePrice: number;
  currencyCode: string;
  discountRateSortKey: number;
  saleStartsAt?: string | null;
  saleEndsAt?: string | null;
  saleCountryCodes: string[];
  likeCount: number;
  shareCount: number;
  isLike: boolean;
  variants: ProductVariantDto[];
  notice?: ProductNoticeDto | null;
}

/**
 * 상품 피드 링크 클릭 귀속 이벤트 응답.
 * 백엔드 `ProductClickEventDto`와 매칭된다.
 */
export interface ProductClickEventDto {
  id: number;
  feedLinkId: number;
  feedId: number;
  productId: number;
  creditedAccountId: number;
  visitorMemberId?: number | null;
  visitorKey?: string | null;
  clickedAt: string;
}

// 백엔드 AttributionController.VISITOR_KEY_HEADER 와 동일해야 한다.
const VISITOR_KEY_HEADER = 'X-LoNe-Visitor-Key';

export default class ProductService {
  /**
   * 상품 단건 조회
   *
   * @param productId 상품 ID
   */
  async get(productId: number) {
    const response = await ky.get(`v1/products/${productId}`);
    return (await response.json()) as ProductDto;
  }

  /**
   * 상품 피드 링크 클릭 귀속 기록
   *
   * 백엔드: POST /v1/product-feed-links/{feedLinkId}/clicks
   *
   * @param feedLinkId 상품 피드 링크 ID (FeedLinkDto.id)
   * @param visitorKey 비로그인 방문자 식별 키 (선택)
   */
  async recordClick(feedLinkId: number, visitorKey?: string) {
    const headers: Record<string, string> = {};
    if (visitorKey) headers[VISITOR_KEY_HEADER] = visitorKey;

    const response = await ky.post(`v1/product-feed-links/${feedLinkId}/clicks`, {
      headers,
    });
    return (await response.json()) as ProductClickEventDto;
  }
}
